import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider } from "@/contexts/AuthContext";
import { ThemeProvider } from "@/components/ThemeProvider";
import { ScrollRestoration } from "@/components/ScrollRestoration";
import Index from "./pages/Index";

// Lazy-loaded pages for code-splitting
const MediatorPage = lazy(() => import("./pages/MediatorPage"));
const MediatorDashboard = lazy(() => import("./pages/MediatorDashboard"));
const SeekerPage = lazy(() => import("./pages/SeekerPage"));
const AuthPage = lazy(() => import("./pages/AuthPage"));
const AdminPage = lazy(() => import("./pages/AdminPage"));
const MediatorStatusPage = lazy(() => import("./pages/MediatorStatusPage"));
const OtherPartyPaymentPage = lazy(() => import("./pages/OtherPartyPaymentPage"));
const FAQPage = lazy(() => import("./pages/FAQPage"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const TermsPage = lazy(() => import("./pages/TermsPage"));
const AssociationPage = lazy(() => import("./pages/AssociationPage"));
const PrivacyPage = lazy(() => import("./pages/PrivacyPage"));
const MediationVoisinagePage = lazy(() => import("./pages/MediationVoisinagePage"));
const MediationTravailPage = lazy(() => import("./pages/MediationTravailPage"));
const MediationFamilialePage = lazy(() => import("./pages/MediationFamilialePage"));
const NotFound = lazy(() => import("./pages/NotFound"));
const MediatorHowItWorksPage = lazy(() => import("./pages/MediatorHowItWorksPage"));
const SeekerProfilePage = lazy(() => import("./pages/SeekerProfilePage"));

const queryClient = new QueryClient();

const App = () => (
  <HelmetProvider>
    <ThemeProvider attribute="class" defaultTheme="system" enableSystem disableTransitionOnChange>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <TooltipProvider>
            <Toaster />
            <Sonner />
            <BrowserRouter>
              <ScrollRestoration />
              <Suspense fallback={<div className="min-h-screen" />}>
                <Routes>
                  <Route path="/" element={<Index />} />
                  <Route path="/connexion" element={<AuthPage />} />
                  <Route path="/mediateur" element={<MediatorPage />} />
                  <Route path="/pour-les-mediateurs" element={<MediatorPage />} />
                  <Route path="/mediateur/statut" element={<MediatorStatusPage />} />
                  <Route path="/mediateur/tableau-de-bord" element={<MediatorDashboard />} />
                  <Route path="/mediateur/comment-ca-marche" element={<MediatorHowItWorksPage />} />
                  <Route path="/trouver-un-mediateur" element={<SeekerPage />} />
                  <Route path="/mon-profil" element={<SeekerProfilePage />} />
                  <Route path="/admin" element={<AdminPage />} />
                  <Route path="/paiement-autre-partie" element={<OtherPartyPaymentPage />} />
                  <Route path="/faq" element={<FAQPage />} />
                  <Route path="/contact" element={<ContactPage />} />
                  <Route path="/conditions" element={<TermsPage />} />
                  <Route path="/association" element={<AssociationPage />} />
                  <Route path="/confidentialite" element={<PrivacyPage />} />
                  <Route path="/mediation-voisinage" element={<MediationVoisinagePage />} />
                  <Route path="/mediation-travail" element={<MediationTravailPage />} />
                  <Route path="/mediation-familiale" element={<MediationFamilialePage />} />
                  
                  {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </Suspense>
            </BrowserRouter>
          </TooltipProvider>
        </AuthProvider>
      </QueryClientProvider>
    </ThemeProvider>
  </HelmetProvider>
);

export default App;
